/**
 * Hariom ERP - Generic CRUD store factory (Zustand + persist)
 *
 * Every module that holds a list of records (products, production orders,
 * leads, invoices, employees, QC tests, …) gets its own persisted store with
 * the same simple API: items + add / update / remove / setAll.
 *
 * Data is saved to the browser (localStorage) so it survives navigation and
 * refresh — exactly like the demand-forecast store. Each store starts EMPTY,
 * so the company's employees fill in the real data themselves.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { sharedStorage } from './sharedStorage'

export interface CrudState<T extends { id: string }> {
  items: T[]
  add: (item: T) => void
  update: (id: string, item: T) => void
  remove: (id: string) => void
  setAll: (items: T[]) => void
}

export function createCrudStore<T extends { id: string }>(storageKey: string) {
  return create<CrudState<T>>()(
    persist(
      (set) => ({
        items: [] as T[],
        add: (item) => set((s) => ({ items: [...s.items, item] })),
        update: (id, item) => set((s) => ({ items: s.items.map((i) => (i.id === id ? item : i)) })),
        remove: (id) => set((s) => ({ items: s.items.filter((i) => i.id !== id) })),
        setAll: (items) => set({ items }),
      }),
      { name: storageKey, storage: createJSONStorage(() => sharedStorage) }
    )
  )
}
